import React, { useEffect, useRef } from "react";

import { REACT_APP_GREENSCREEN_API_URL } from "env";

import { InputValues, WidgetsConfiguration } from "../types/Greenscreens.types";

interface GreenscreensWidgetProps {
  accessTokenProvider: () => string;
  user: { email: string };
  inputValues: InputValues;
  widgets: WidgetsConfiguration;
}

declare global {
  interface Window {
    Greenscreens?: {
      Rates: {
        build: (options: {
          container: HTMLElement;
          baseApiPath: string;
          accessTokenProvider: () => string;
          user: { email: string };
          widgets: WidgetsConfiguration;
        }) => {
          render: (inputValues: InputValues) => void;
          destroy?: () => void;
        };
      };
    };
  }
}

const GreenscreensWidget: React.FC<GreenscreensWidgetProps> = ({ accessTokenProvider, user, inputValues, widgets }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<{
    render: (input: InputValues) => void;
    destroy?: () => void;
  } | null>(null);

  useEffect(() => {
    const scriptId = "greenscreens-portable-bundle";
    const baseApiPath = REACT_APP_GREENSCREEN_API_URL;
    const loadWidget = () => {
      if (!window.Greenscreens?.Rates?.build || !containerRef.current) return;

      try {
        const renderer = window.Greenscreens.Rates.build({
          container: containerRef.current,
          baseApiPath,
          accessTokenProvider,
          user,
          widgets,
        });

        rendererRef.current = renderer;
        renderer.render(inputValues);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error("There was an error building Greenscreens Widget: " + message);
      }
    };

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.src = "https://gs-portable.s3.amazonaws.com/rates.bundle.js";
      script.id = scriptId;
      script.async = true;
      script.onload = loadWidget;
      document.body.appendChild(script);
    } else {
      loadWidget();
    }

    return () => {
      rendererRef.current?.destroy?.();
    };
  }, [accessTokenProvider, user, inputValues, widgets]);

  return (
    <div
      ref={containerRef}
      style={{ minHeight: 300, backgroundColor: "#F5F6F7", display: "flex", alignItems: "center" }}
    />
  );
};

export default GreenscreensWidget;
